// Newtmail, the new Workpeg mail app, and how this app points people at it
// while the two overlap (NM-REL-07): a tile in the app switcher and a
// "Try Newtmail" banner over the mail list, both only for a business that
// has Newtmail installed.
//
// Every link to Newtmail is built here, from a fixed origin and a business
// id that must be a uuid. Nothing the api or the page says can turn one into
// a javascript:, data: or look-alike link, or add a path or a query to it:
// a business id that is not a uuid gives no link at all, and then neither
// the tile nor the banner is drawn.

// DEC-02: newtmail.workpeg.com is the app's only host.
export const NEWTMAIL_ORIGIN = 'https://newtmail.workpeg.com';

// The peg's name in the api's installed-pegs answer
// (GET businesses/<uuid>/pegs/ -> [{ peg: { name } }]).
export const NEWTMAIL_PEG = 'newtmail';

// The views of Newtmail this app links to.
const VIEWS = Object.freeze(['inbox', 'spam']);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isBusinessUuid = (value) => typeof value === 'string' && UUID.test(value);

// '/b/<business>/<view>' in Newtmail, or null for a business that is not a
// uuid or a view this app does not link to.
export const newtmailPath = (business, view) => {
  if (!VIEWS.includes(view) || !isBusinessUuid(business)) return null;
  return `/b/${business.toLowerCase()}/${view}`;
};

// Newtmail's Inbox for `business`, or null.
export const newtmailInboxUrl = (business) => {
  const path = newtmailPath(business, 'inbox');
  return path && `${NEWTMAIL_ORIGIN}${path}`;
};

// Whether the api's installed-pegs answer lists the peg called `name`. The
// answer is the one the api gave this person for this business, so a
// business that hides Newtmail from a person, or has not installed it,
// shows neither the tile nor the banner.
export const hasInstalledPeg = (installed, name) => Array.isArray(installed)
  && installed.some((install) => install !== null && typeof install === 'object'
    && install.peg !== null && typeof install.peg === 'object' && install.peg.name === name);

export const isNewtmailInstalled = (installed) => hasInstalledPeg(installed, NEWTMAIL_PEG);

// Where the app switcher's Newtmail tile goes, or null for no tile: the
// business's Inbox in Newtmail, when the installed-pegs answer lists it.
export const newtmailTileUrl = (installed, business) => (
  isNewtmailInstalled(installed) ? newtmailInboxUrl(business) : null
);

// Newtmail's compact mark on the CDN, one per theme, as every peg's launcher
// tile shows it (pegs/<namespace>/assets/logo/secondary-<theme>.svg).
export const NEWTMAIL_LOGOS = Object.freeze({
  light: 'https://cdn.workpeg.com/pegs/newtmail/assets/logo/secondary-light.svg',
  dark: 'https://cdn.workpeg.com/pegs/newtmail/assets/logo/secondary-dark.svg',
});

// --- the "Try Newtmail" banner ----------------------------------------------
// Dismissing it hides it for this person in this business, in this browser.
// localStorage can be missing or throw (a private window, blocked site
// data); then the banner still works and a dismissal lasts until the page
// is reloaded.
export const BANNER_KEY_PREFIX = 'newtmail-banner-dismissed';

// The key a dismissal is kept under, or null when the business is not a uuid.
// `user` is the signed-in person's email: the api's users/me has no id.
export const bannerKey = (business, user) => {
  if (!isBusinessUuid(business)) return null;
  const person = typeof user === 'string' ? user.trim().toLowerCase() : '';
  return `${BANNER_KEY_PREFIX}:${business.toLowerCase()}:${person}`;
};

export const isBannerDismissed = (storage, business, user) => {
  const key = bannerKey(business, user);
  if (!key) return false;
  try {
    return storage.getItem(key) === '1';
  } catch {
    return false;
  }
};

// Remembers the dismissal; false when it could not be kept.
export const rememberBannerDismissed = (storage, business, user) => {
  const key = bannerKey(business, user);
  if (!key) return false;
  try {
    storage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
};

// The banner's link, or null when there is to be no banner: Newtmail is not
// installed, the business is not a uuid, or this person dismissed it here.
export const tryNewtmailLink = ({ installed, business, user, storage }) => {
  const url = newtmailTileUrl(installed, business);
  if (!url || isBannerDismissed(storage, business, user)) return null;
  return url;
};
